import { rebuildRostersFromCsvs, type UploadKind, type UploadedFile } from '../Data/uploadedFiles';
import { fetchUploadCsv, fetchUploads } from '../Data/uploadsApi';
import { extractStudentsFromText, type FileType } from './extractStudents';
import { FILE_TYPE_OPTIONS, type SelectableFileType } from './fileTypes';
import { schoolYearForDate } from '../../core/schoolYear';
import {
  applySavedSisMapping,
  readSisColumns,
  sisColumnOverrides,
  type SisColumn,
  type SisMapping,
} from './sisFieldMapping';
import { hydrateSisMappings } from '../Data/sisMappingApi';

export type SelectedFile = {
  id: string;
  file: File;
  valid: boolean;
  persisted?: boolean;
  storedKind?: UploadKind;
  deleting?: boolean;
  type?: FileType;
  studentCount?: number;
  csvText?: string;
  sisColumns?: SisColumn[];
  sisMapping?: SisMapping;
};

const BURST_WINDOW_MS = 30_000;

const stampMs = (u: UploadedFile): number | null => {
  if (u.uploadedAt == null) return null;
  const ms = Date.parse(u.uploadedAt);
  return Number.isNaN(ms) ? null : ms;
};

const BATCH_KEY = 'bamboo.uploadStagedBatch.v1';

function batchStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export type StagedBatchEntry = { id: string; type?: FileType };

export function saveStagedBatch(entries: StagedBatchEntry[]): void {
  try {
    batchStorage()?.setItem(BATCH_KEY, JSON.stringify(entries));
  } catch {
    return;
  }
}

export function saveStagedBatchIds(ids: string[]): void {
  saveStagedBatch(ids.map((id) => ({ id })));
}

export function loadStagedBatch(): StagedBatchEntry[] | null {
  try {
    const raw = batchStorage()?.getItem(BATCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((entry) =>
      typeof entry === 'string' ? { id: entry } : (entry as StagedBatchEntry),
    );
  } catch {
    return null;
  }
}

export function loadStagedBatchIds(): string[] | null {
  return loadStagedBatch()?.map((e) => e.id) ?? null;
}

export function selectRehydrationSources(
  uploads: UploadedFile[],
  year: string,
  stagedIds?: string[] | null,
): UploadedFile[] {
  if (stagedIds != null) {
    const order = new Map(stagedIds.map((id, i) => [id, i]));
    return uploads
      .filter((u) => order.has(u.id) && u.schoolYear === year)
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }
  const stamped = uploads
    .filter((u) => u.schoolYear === year)
    .map((u) => ({ upload: u, ms: stampMs(u) }))
    .filter((e): e is { upload: UploadedFile; ms: number } => e.ms != null);
  const newest = stamped.reduce<number | null>((max, e) => (max == null || e.ms > max ? e.ms : max), null);
  if (newest == null) return [];
  return stamped
    .filter((e) => newest - e.ms <= BURST_WINDOW_MS)
    .sort((a, b) => a.ms - b.ms || a.upload.name.localeCompare(b.upload.name))
    .map((e) => e.upload);
}

export async function rehydrateUploadDraft(): Promise<SelectedFile[]> {
  await hydrateSisMappings();
  try {
    const uploads = await fetchUploads();
    const now = new Date();
    const year = schoolYearForDate({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    });
    const staged = loadStagedBatch();
    const typeById = new Map((staged ?? []).map((e) => [e.id, e.type]));
    const sources = selectRehydrationSources(uploads, year, staged?.map((e) => e.id) ?? null);
    return await Promise.all(
      sources.map(async (source) => {
        const { csv } = await fetchUploadCsv(source.id);
        return buildSelectedFile(
          { id: source.id, name: source.name, kind: source.kind, csv },
          typeById.get(source.id),
        );
      }),
    );
  } catch (e) {
    console.error('upload: rehydrating the staged list failed', e);
    return [];
  }
}

let rebuildGeneration = 0;

export function invalidateStagedRosterRebuilds(): void {
  rebuildGeneration += 1;
}

export type RebuildOutcome = 'rebuilt' | 'superseded' | 'failed';

export async function rebuildStagedRosterCaches(
  excludeIds: ReadonlySet<string>,
): Promise<RebuildOutcome> {
  const gen = ++rebuildGeneration;
  try {
    const uploads = await fetchUploads();
    const wanted = uploads.filter(
      (u): u is UploadedFile & { kind: UploadKind } => u.kind != null && !excludeIds.has(u.id),
    );
    const sources = await Promise.all(
      wanted.map(async (u) => ({
        csv: (await fetchUploadCsv(u.id)).csv,
        kind: u.kind,
        schoolYear: u.schoolYear,
        uploadedAt: u.uploadedAt,
      })),
    );
    if (gen !== rebuildGeneration) return 'superseded';
    rebuildRostersFromCsvs(sources);
    return 'rebuilt';
  } catch (e) {
    if (gen !== rebuildGeneration) return 'superseded';
    console.error('upload: failed to rebuild rosters after a delete', e);
    rebuildRostersFromCsvs([]);
    return 'failed';
  }
}

export type RehydrationContent = {
  id: string;
  name: string;
  kind?: UploadKind;
  csv: string;
};

function rebuiltFile(csv: string, name: string): File {
  const file = new File([csv], name, { type: 'text/csv' });
  file.text = () => Promise.resolve(csv);
  return file;
}

function selectableType(type: FileType | undefined): SelectableFileType | undefined {
  if (type === 'sis') return 'student_roster';
  return FILE_TYPE_OPTIONS.some((o) => o.value === type)
    ? (type as SelectableFileType)
    : undefined;
}

export function buildSelectedFile(
  { id, name, kind, csv }: RehydrationContent,
  markerType?: FileType,
): SelectedFile {
  const file = rebuiltFile(csv, name);
  const type = selectableType(markerType) ?? kind;
  if (type == null) {
    return { id, file, valid: true, persisted: true, storedKind: kind };
  }
  const base: SelectedFile = {
    id,
    file,
    valid: true,
    persisted: true,
    storedKind: kind,
    type,
    studentCount: extractStudentsFromText(csv, type).rows.length,
  };
  if (type !== 'student_roster') return base;
  const sisColumns = readSisColumns(csv);
  const saved = applySavedSisMapping(sisColumns);
  const sisMapping = saved.kind === 'exact' ? saved.mapping : undefined;
  return {
    ...base,
    csvText: csv,
    sisColumns,
    sisMapping,
    studentCount: sisMapping
      ? extractStudentsFromText(csv, 'sis', sisColumnOverrides(sisMapping)).rows.length
      : base.studentCount,
  };
}
